import type { BoardData, BuiltinLayerKey, Item, ItemKind, Layer } from './schema.js';
import { builtinLayerId, LAYER_NAMES } from './layers.js';

/**
 * Layers split out of a former combined one (OIL-51): stands from "Mics & stands", risers from
 * "Stage & risers", sub-cores from "Stage boxes & sub-cores" – so stands can be hidden on their own.
 */
export const LAYER_SPLITS: readonly {
  key: BuiltinLayerKey;
  parent: BuiltinLayerKey;
  kinds: readonly ItemKind[];
  /** Former default name of the parent; renamed to the new default only if unchanged. */
  oldParentName: string;
}[] = [
  { key: 'stands', parent: 'microphones', kinds: ['stand'], oldParentName: 'Mics & Stands' },
  { key: 'risers', parent: 'stage', kinds: ['riser'], oldParentName: 'Stage & Risers' },
  {
    key: 'subcores',
    parent: 'stageboxes',
    kinds: ['subcore'],
    oldParentName: 'Stage boxes & Sub-cores',
  },
];

/** The combined layer a split-out one belongs to (topics of catch-up and open items). */
export function splitParentKey(key: BuiltinLayerKey): BuiltinLayerKey {
  return LAYER_SPLITS.find((s) => s.key === key)?.parent ?? key;
}

export interface LayerSplitChanges {
  /** New or renamed layers (orders renumbered when a layer is inserted). */
  layers: Layer[];
  /** Items moved from the parent layer to the new one. */
  items: Item[];
}

/**
 * What the split changes on a board (idempotent – empty when already done): the new layers are
 * inserted right after their parent (same visibility and lock), objects of their kinds move
 * there from the parent, unchanged default parent names get the new default names.
 */
export function layerSplitChanges(data: Pick<BoardData, 'layers' | 'items'>): LayerSplitChanges {
  const layers = Object.values(data.layers).map((l) => ({ ...l }));
  const changed = new Set<string>();
  for (const s of LAYER_SPLITS) {
    const parentId = builtinLayerId(s.parent);
    const parent = layers.find((l) => l.id === parentId);
    if (parent === undefined) continue;
    if (parent.name === s.oldParentName) {
      parent.name = LAYER_NAMES[s.parent];
      changed.add(parent.id);
    }
    const id = builtinLayerId(s.key);
    if (layers.some((l) => l.id === id)) continue;
    // make room right after the parent
    for (const l of layers) {
      if (l.order > parent.order) {
        l.order += 1;
        changed.add(l.id);
      }
    }
    layers.push({
      id,
      key: s.key,
      name: LAYER_NAMES[s.key],
      visible: parent.visible,
      locked: parent.locked,
      order: parent.order + 1,
    });
    changed.add(id);
  }
  const byKey = new Map(LAYER_SPLITS.flatMap((s) => s.kinds.map((k) => [k, s] as const)));
  const items = Object.values(data.items).flatMap((i) => {
    const s = byKey.get(i.kind);
    return s !== undefined && i.layerId === builtinLayerId(s.parent)
      ? [{ ...i, layerId: builtinLayerId(s.key) }]
      : [];
  });
  return { layers: layers.filter((l) => changed.has(l.id)), items };
}

/** Applies the split to a plain board (document migration v2 → v3, portable files). */
export function splitLayers(data: BoardData): BoardData {
  const c = layerSplitChanges(data);
  return {
    ...data,
    layers: { ...data.layers, ...Object.fromEntries(c.layers.map((l) => [l.id, l])) },
    items: { ...data.items, ...Object.fromEntries(c.items.map((i) => [i.id, i])) },
  };
}
